import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { useLanguage } from "./LanguageContext";
import { FiAlertTriangle } from "react-icons/fi";

const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
};

const messages = {
  invalid_token: { ka: "არასწორი ტოკენი.", en: "Invalid token." },
  not_found_or_used: { ka: "ტოკენი არ არსებობს ან უკვე გამოყენებულია.", en: "Token not found or already used." },
  already_exists: { ka: "ანგარიში უკვე არსებობს ამ იმეილით/ნომრით.", en: "An account with this email/phone already exists." },
  tx_failed: { ka: "ვერ მოხერხდა დადასტურება. სცადეთ მოგვიანებით.", en: "Confirmation failed. Please try again later." },
  unknown: { ka: "უცნობი შეცდომა.", en: "Unknown error." },
};

// Helper: normalize ?lang
function getLangFromQuery() {
  const q = new URLSearchParams(window.location.search).get("lang") || "";
  const v = q.toLowerCase();
  return v === "ka" || v === "en" ? v : null;
}

export default function VerifyFailed() {
  // თუ გაქვს setLanguage კონტექსტში, გამოვიყენოთ; თუ არა — არაფერი
  const ctx = useLanguage() || {};
  const languageFromCtx = ctx.language || "en";
  const setLanguage = ctx.setLanguage;

  const langFromQS = useMemo(getLangFromQuery, []);
  const effectiveLang = langFromQS || languageFromCtx; // URL > context

  // სურვილისამებრ: გლობალურადაც გადართე, თუ პარამეტრი მოვიდა
  useEffect(() => {
    if (langFromQS && setLanguage && languageFromCtx !== langFromQS) {
      setLanguage(langFromQS);
    }
  }, [langFromQS, setLanguage, languageFromCtx]);

  const T = (ka, en) => (effectiveLang === "ka" ? ka : en);

  useEffect(() => {
    document.title =
      effectiveLang === "ka"
        ? "ვერ მოხერხდა დადასტურება | Shopaholics"
        : "Verification failed | Shopaholics";
  }, [effectiveLang]);

  const sp = new URLSearchParams(window.location.search);
  const code = (sp.get("code") || "unknown").toLowerCase();
  const entry = messages[code] || messages.unknown;
  const text = effectiveLang === "ka" ? entry.ka : entry.en;

  // preserve ?lang=...
  const langSuffix = `?lang=${effectiveLang}`;

  return (
    <Page>
      <Card role="alert" aria-live="polite">
        <Header>
          <IconWrap $danger>
            <FiAlertTriangle />
          </IconWrap>
          <div>
            <Title>{T("დადასტურება ვერ განხორციელდა", "Verification failed")}</Title>
            <Subtle>{text}</Subtle>
          </div>
        </Header>

        <Hint>
          <Bullet />
          <span>
            {T(
              "თუ პრობლემა გრძელდება, სცადეთ თავიდან ან დაუკავშირდით მხარდაჭერას.",
              "If the problem persists, try again or contact support."
            )}
          </span>
        </Hint>

        <Actions>
          <Primary as="a" href={`/${langSuffix}`}>
            {T("მთავარზე დაბრუნება", "Back to home")}
          </Primary>
          <Ghost as="a" href={`/login${langSuffix}`}>
            {T("შესვლა", "Log in")}
          </Ghost>
        </Actions>
      </Card>
    </Page>
  );
}

/* ====== styles (unchanged) ====== */

const Page = styled.main`
  background: ${COLORS.subtle};
  padding: clamp(12px, 4vw, 24px);
  display: grid;
  justify-items: center;
  align-content: center;
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 640px) {
    align-content: start;
    padding-top: max(12px, env(safe-area-inset-top));
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }
`;

const Card = styled.section`
  width: min(560px, 100%);
  background: ${COLORS.bg};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0,0,0,.08);
  padding: clamp(16px, 3.5vw, 28px);
  display: grid;
  gap: 14px;
  max-height: 100%;
  overflow: auto;
`;

const Header = styled.header`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const IconWrap = styled.div`
  width: 40px; height: 40px;
  border-radius: 12px;
  display: grid; place-items: center;
  font-size: 20px;
  color: #fff;
  background: ${({ $danger }) => ($danger ? "#dc2626" : COLORS.brand)};
  box-shadow: 0 8px 20px rgba(0,0,0,.08);
  flex: 0 0 auto;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(18px, 2.6vw, 22px);
  font-weight: 900;
  color: ${COLORS.text};
`;

const Subtle = styled.p`
  margin: 4px 0 0;
  color: ${COLORS.dim};
  font-size: 14px;
`;

const Hint = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  color: ${COLORS.text};
  background: #fff7f7;
  border: 1px solid #ffe1e1;
  border-radius: 12px;
  padding: 10px 12px;
`;

const Bullet = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: #dc2626; margin-top: 6px; flex: 0 0 auto;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 4px;
  a { flex: 1 1 180px; text-align: center; }
  @media (max-width: 420px) { a { flex: 1 1 100%; } }
`;

const ButtonBase = styled.a`
  height: 42px;
  padding: 0 16px;
  border-radius: 10px;
  font-weight: 800;
  font-size: 14px;
  text-decoration: none;
  display: inline-flex; align-items: center; justify-content: center;
  transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.08); }
`;

const Primary = styled(ButtonBase)`
  background: ${COLORS.brand};
  color: #fff;
  border: 1px solid ${COLORS.brand};
`;

const Ghost = styled(ButtonBase)`
  background: #fff;
  color: ${COLORS.text};
  border: 1px solid ${COLORS.line};
`;
  